import { useCallback, useEffect, useRef, useState } from 'react';

import { getLocations } from 'lib/api/getLocations';
import { TMSLocation } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';

type UseCustomerLocationsParams = {
  tmsID: Undef<number>;
  customerID: Undef<number>;
  customerExternalTmsId: Undef<string>;
};

// Customer-scoped TMS locations (address book favorites) for a TMS integration.
// Reads the nightly/webhook-synced list only; never forces an on-demand refresh,
// which hangs for customers with large address books.
export function useCustomerLocations({
  tmsID,
  customerID,
  customerExternalTmsId,
}: UseCustomerLocationsParams): {
  locations: Maybe<TMSLocation[]>;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  isLoadingLocations: boolean;
  hasCustomer: boolean;
} {
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const requestIdRef = useRef(0);

  const customerKey = `${customerID ?? ''}:${customerExternalTmsId ?? ''}`;
  const hasCustomer = Boolean(customerID || customerExternalTmsId);

  const loadedCustomerKeyRef = useRef(customerKey);
  if (loadedCustomerKeyRef.current !== customerKey) {
    loadedCustomerKeyRef.current = customerKey;
    requestIdRef.current += 1;
    setLocations(hasCustomer ? [] : null);
  }

  const fetchLocations = useCallback(async () => {
    if (!tmsID || (!customerID && !customerExternalTmsId)) {
      requestIdRef.current += 1;
      setIsLoadingLocations(false);
      setLocations(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    const requestCustomerKey = `${customerID ?? ''}:${customerExternalTmsId ?? ''}`;
    setIsLoadingLocations(true);
    const res = await getLocations(
      tmsID,
      false,
      false,
      customerID ? undefined : customerExternalTmsId,
      customerID
    );

    if (
      requestId !== requestIdRef.current ||
      requestCustomerKey !== loadedCustomerKeyRef.current
    ) {
      return;
    }

    setIsLoadingLocations(false);
    if (res.isOk()) {
      setLocations(res.value.locationList ?? []);
      return;
    }
    setLocations([]);
  }, [tmsID, customerID, customerExternalTmsId]);

  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations, customerKey]);

  return { locations, setLocations, isLoadingLocations, hasCustomer };
}
